import { Eye, EyeOff } from "lucide-react";
import { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface InputProps extends Omit<
	React.InputHTMLAttributes<HTMLInputElement>,
	"size" | "prefix"
> {
	size?: "small" | "middle" | "large";
	prefix?: React.ReactNode;
	suffix?: React.ReactNode;
	addonBefore?: React.ReactNode;
	addonAfter?: React.ReactNode;
	showCount?: boolean;
	maxLength?: number;
	status?: "error" | "warning";
	allowClear?: boolean;
	onClear?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			className,
			size = "middle",
			prefix,
			suffix,
			addonBefore,
			addonAfter,
			showCount,
			maxLength,
			status,
			allowClear,
			onClear,
			value,
			onChange,
			disabled,
			...props
		},
		ref,
	) => {
		const sizeStyles = {
			small: "h-7 text-sm",
			middle: "h-9 text-sm",
			large: "h-11 text-base",
		};

		const statusStyles = {
			error: "border-error-500 focus:border-error-500 focus:ring-error-500/20",
			warning: "border-warning-500 focus:border-warning-500 focus:ring-warning-500/20",
		};

		const inputElement = (
			<div className="relative flex items-center flex-1">
				{prefix && (
					<span className="absolute left-3 text-gray-400 pointer-events-none">{prefix}</span>
				)}
				<input
					ref={ref}
					disabled={disabled}
					value={value}
					onChange={onChange}
					maxLength={maxLength}
					className={cn(
						"w-full rounded-md border border-gray-300 bg-white px-3 transition-colors",
						"focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20",
						"disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500",
						"dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100",
						"dark:focus:border-primary-500 dark:disabled:bg-gray-900",
						"placeholder:text-gray-400 dark:placeholder:text-gray-500",
						sizeStyles[size],
						prefix && "pl-9",
						(suffix || allowClear || showCount) && "pr-9",
						status && statusStyles[status],
						className,
					)}
					{...props}
				/>
				{(suffix || allowClear || showCount) && (
					<span className="absolute right-3 flex items-center gap-1 text-gray-400">
						{allowClear && value && (
							<button
								type="button"
								onClick={onClear}
								className="hover:text-gray-600 dark:hover:text-gray-300"
							>
								×
							</button>
						)}
						{showCount && maxLength && (
							<span className="text-xs">
								{String(value || "").length}/{maxLength}
							</span>
						)}
						{suffix}
					</span>
				)}
			</div>
		);

		if (addonBefore || addonAfter) {
			return (
				<div className="flex">
					{addonBefore && (
						<span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 rounded-l-md dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400">
							{addonBefore}
						</span>
					)}
					<div
						className={cn(
							"flex-1",
							addonBefore && "[&_input]:rounded-l-none",
							addonAfter && "[&_input]:rounded-r-none",
						)}
					>
						{inputElement}
					</div>
					{addonAfter && (
						<span className="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 rounded-r-md dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400">
							{addonAfter}
						</span>
					)}
				</div>
			);
		}

		return inputElement;
	},
);

Input.displayName = "Input";

// TextArea Component
export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	showCount?: boolean;
	maxLength?: number;
	status?: "error" | "warning";
	autoSize?: boolean | { minRows?: number; maxRows?: number };
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	({ className, showCount, maxLength, status, autoSize, value, disabled, ...props }, ref) => {
		const statusStyles = {
			error: "border-error-500 focus:border-error-500 focus:ring-error-500/20",
			warning: "border-warning-500 focus:border-warning-500 focus:ring-warning-500/20",
		};

		const getAutoSizeStyle = () => {
			if (!autoSize) return {};
			if (autoSize === true) return { resize: "none" as const };
			return {
				resize: "none" as const,
				minHeight: autoSize.minRows ? `${autoSize.minRows * 1.5}em` : undefined,
				maxHeight: autoSize.maxRows ? `${autoSize.maxRows * 1.5}em` : undefined,
			};
		};

		return (
			<div className="relative">
				<textarea
					ref={ref}
					disabled={disabled}
					value={value}
					maxLength={maxLength}
					className={cn(
						"w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-colors",
						"focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20",
						"disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500",
						"dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100",
						"dark:focus:border-primary-500 dark:disabled:bg-gray-900",
						"placeholder:text-gray-400 dark:placeholder:text-gray-500",
						status && statusStyles[status],
						showCount && "pb-6",
						className,
					)}
					style={getAutoSizeStyle()}
					{...props}
				/>
				{showCount && maxLength && (
					<span className="absolute bottom-2 right-3 text-xs text-gray-400">
						{String(value || "").length}/{maxLength}
					</span>
				)}
			</div>
		);
	},
);

TextArea.displayName = "TextArea";

// Password Component
export interface PasswordProps extends Omit<InputProps, "type"> {
	visibilityToggle?: boolean;
}

const Password = forwardRef<HTMLInputElement, PasswordProps>(
	({ visibilityToggle = true, suffix, ...props }, ref) => {
		const [visible, setVisible] = useState(false);

		const toggleVisibility = () => setVisible(!visible);

		const passwordSuffix = visibilityToggle ? (
			<button
				type="button"
				onClick={toggleVisibility}
				className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
			>
				{visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
			</button>
		) : null;

		return (
			<Input
				ref={ref}
				type={visible ? "text" : "password"}
				suffix={
					<>
						{passwordSuffix}
						{suffix}
					</>
				}
				{...props}
			/>
		);
	},
);

Password.displayName = "Password";

// Search Component
export interface SearchProps extends InputProps {
	onSearch?: (value: string) => void;
	enterButton?: React.ReactNode;
	loading?: boolean;
}

const Search = forwardRef<HTMLInputElement, SearchProps>(
	({ onSearch, enterButton, loading: _loading, className, ...props }, ref) => {
		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter" && onSearch) {
				onSearch((e.target as HTMLInputElement).value);
			}
		};

		return (
			<Input
				ref={ref}
				className={cn(enterButton && "rounded-r-none", className)}
				onKeyDown={handleKeyDown}
				{...props}
			/>
		);
	},
);

Search.displayName = "Search";

// Compound components
interface InputComponent extends React.ForwardRefExoticComponent<
	InputProps & React.RefAttributes<HTMLInputElement>
> {
	TextArea: typeof TextArea;
	Password: typeof Password;
	Search: typeof Search;
}

const InputWithCompound = Input as InputComponent;
InputWithCompound.TextArea = TextArea;
InputWithCompound.Password = Password;
InputWithCompound.Search = Search;

export { InputWithCompound as Input, TextArea, Password, Search };
